/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow local-strict
*/

import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import ActionTypes from '@ziynx/action-types';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Button } from '@ziynx/components/buttons';
import Colors from '@ziynx/theme/colors';
import Input from '@ziynx/components/inputs';
import Dropdown from '@ziynx/components/selects';
import { ContentContainer } from '@ziynx/components/layout';
import { Form, FormRow } from '@ziynx/components/forms';
import { Body } from '@ziynx/components/typography';
import { withAppManager, useNotification } from '@ziynx/components/utilities';
import type { Viewports } from '@ziynx/flow-types/globals';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import { CONTACT_CATEGORIES } from '../../constants';

type Props = $ReadOnly<{|
    /**
     * Navigation stack
     */
    history: Object,
    /**
     * Viewports
     */
    viewport: Viewports
|}>;

type FormData = {
  category: string,
  message: string,
};

type DispatchType = ({
  type: string,
  body?: {
    title?: string,
    description?: string,
   },
}) => void;

const PageWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: ${({ isMobileSmall }) => (isMobileSmall ? '40px 16px' : '40px')};
`;

const PageContentWrapper = styled.div`
  width: 100%;
  max-width: 688px;
`;

const StyledBody = styled(Body)`
    margin-bottom: 48px;
`;

const SupportIntakeScreen = (props: ManagedProps<Props>) => {
  const { viewport } = props;
  const {
    handleSubmit, control, errors, clearErrors, setError, reset, formState: { dirtyFields },
  } = useForm();

  const { notificationDispatch } = useNotification();

  const isMobileSmall = viewport === 'mobileSmall';

  // Set up dispatch
  const dispatch: DispatchType = useDispatch();
  const intakeService = useSelector(({ intake }) => intake ?? {}, shallowEqual);

  const resetNotification = () => {
    setTimeout(() => {
      dispatch({
        type: ActionTypes.intake.clearIntake,
      });
    }, 6000);
  };

  useEffect(() => {
    if (intakeService?.success && !intakeService?.posting) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'success',
          content: 'Your help request has been submitted.',
        },
      });
      resetNotification();
      reset({
        category: ' ',
        message: '',
      });
      const messageInputWrapper = document.forms[0].querySelectorAll('div')[6];
      const charLimit = messageInputWrapper.childNodes[2].childNodes[0].childNodes[0];
      charLimit.innerText = '0 / 1000';
    }
    if (intakeService?.error && !intakeService?.success) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'error',
          content: 'We were unable to submit your help request. Please, try again.',
        },
      });
      resetNotification();
    }
  }, [intakeService?.error, intakeService?.success, intakeService?.posting]);

  const isDisabled = (): boolean => {
    if (!dirtyFields || intakeService?.posting || !!errors?.category) return true;
    return !dirtyFields.category || !dirtyFields.message;
  };

  const onSubmit = (data: FormData): void => {
    if (!data?.category) {
      setError('category', {
        type: 'manual',
        // message: 'Must include category',
      });
    }
    dispatch({
      type: ActionTypes.intake.postIntakeSubmission.START,
      body: {
        title: 'Web App (user-submitted)',
        description: `${data?.category} - ${data?.message}`,
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>Support Intake form | ZIYNX</title>
      </Helmet>
      <ContentContainer>
        <PageWrapper isMobileSmall={isMobileSmall}>
          <PageContentWrapper>
            <Form
              formID="contact-support"
              title="We're here to help"
              width="100%"
              headingPaddingBottom="12px"
            >
              <StyledBody medium color={Colors.GrayDark}>
                Share the details of your question, feedback, or request below.
              </StyledBody>
              <FormRow paddingBottom="40px">
                <Controller
                  control={control}
                  rules={{ required: true }}
                  render={({ onChange, value }) => (
                    <Dropdown
                      inputName="category"
                      activeLabel="Category"
                      label="Category"
                      error={!!errors?.category}
                      errorLabel="Please choose a category."
                      disabled={intakeService?.posting}
                      required
                      value={value}
                      options={CONTACT_CATEGORIES}
                      onFocus={() => clearErrors(['category'])}
                      onChange={(event: SyntheticInputEvent<HTMLInputElement>): void => {
                        (event?.target: HTMLInputElement);
                        onChange(event);
                      }}
                    />
                  )}
                  name="category"
                  defaultValue=" "
                />
              </FormRow>
              <FormRow height="100%" paddingBottom="20px">
                <Controller
                  control={control}
                  rules={{ required: true }}
                  render={({ onChange, value }) => (
                    <Input
                      height="248px"
                      variant="textarea"
                      inputName="message"
                      label="Message"
                      error={!!errors?.message}
                      errorLabel="Please enter a message."
                      charLimit={1000}
                      maxLength={1000}
                      disabled={intakeService?.posting}
                      required
                      value={value}
                      onChange={(event: SyntheticInputEvent<HTMLInputElement>): void => {
                        (event?.target: HTMLInputElement);
                        onChange(event);
                      }}
                    />
                  )}
                  name="message"
                  defaultValue=""
                />
              </FormRow>
              <FormRow>
                <Button onClick={handleSubmit(onSubmit)} variant="primary" disabled={isDisabled()} isLoading={intakeService?.posting} width="100%">
                  Submit
                </Button>
              </FormRow>
            </Form>
          </PageContentWrapper>
        </PageWrapper>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(SupportIntakeScreen): React$ComponentType<ManagedProps<Props>>);
